(() => {
  'use strict';

  const progress = window.NProgress;
  if (!progress) return;

  const pendingNavigationKey = 'fwd:navigation-progress';
  let headerResizeObserver;
  let isActive = false;
  let positionUpdatePending = false;

  progress.configure({
    barSelector: '[role="progressbar"]',
    minimum: 0.12,
    showSpinner: false,
    speed: 200,
    template:
      '<div class="bar" role="progressbar" aria-label="페이지 불러오는 중"><div class="peg"></div></div>',
    trickleSpeed: 200,
  });

  function updatePendingNavigation(isPending) {
    try {
      if (isPending) {
        window.sessionStorage.setItem(pendingNavigationKey, '1');
      } else {
        window.sessionStorage.removeItem(pendingNavigationKey);
      }
    } catch (_error) {
      // Storage can be unavailable in private contexts. The current-page bar still works.
    }
  }

  function hasPendingNavigation() {
    try {
      return window.sessionStorage.getItem(pendingNavigationKey) === '1';
    } catch (_error) {
      return false;
    }
  }

  function updateNavigationProgressPosition() {
    const header = document.querySelector('.section-header');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    document.documentElement.style.setProperty('--navigation-progress-top', `${headerBottom}px`);
  }

  function scheduleNavigationProgressPositionUpdate() {
    if (positionUpdatePending) return;

    positionUpdatePending = true;
    window.requestAnimationFrame(() => {
      positionUpdatePending = false;
      updateNavigationProgressPosition();
    });
  }

  function startNavigationProgress() {
    if (isActive || progress.isStarted()) return;

    isActive = true;
    updatePendingNavigation(true);
    updateNavigationProgressPosition();
    progress.start();
  }

  function finishNavigationProgress() {
    isActive = false;
    updatePendingNavigation(false);
    progress.done();
  }

  function hasDisabledProgress(element) {
    return Boolean(element?.closest?.('[data-navigation-progress="false"]'));
  }

  function isInternalPageLink(anchor, event) {
    if (
      !anchor ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      anchor.hasAttribute('download') ||
      hasDisabledProgress(anchor)
    ) {
      return false;
    }

    const target = (anchor.getAttribute('target') || '').toLowerCase();
    if (target && target !== '_self') return false;

    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('#')) return false;

    let destination;
    try {
      destination = new URL(href, window.location.href);
    } catch (_error) {
      return false;
    }

    if (!['http:', 'https:'].includes(destination.protocol) || destination.origin !== window.location.origin) {
      return false;
    }

    const isSameDocument =
      destination.pathname === window.location.pathname && destination.search === window.location.search;

    return !(isSameDocument && destination.hash);
  }

  function isPageForm(form) {
    if (!form || form.tagName !== 'FORM' || hasDisabledProgress(form)) return false;

    const target = (form.getAttribute('target') || '').toLowerCase();
    if (target && target !== '_self') return false;
    if ((form.getAttribute('method') || '').toLowerCase() === 'dialog') return false;

    try {
      const destination = new URL(form.getAttribute('action') || window.location.href, window.location.href);
      return ['http:', 'https:'].includes(destination.protocol) && destination.origin === window.location.origin;
    } catch (_error) {
      return false;
    }
  }

  function afterEventDispatch(callback) {
    if (typeof window.queueMicrotask === 'function') {
      window.queueMicrotask(callback);
      return;
    }

    Promise.resolve().then(callback);
  }

  updateNavigationProgressPosition();

  const header = document.querySelector('.section-header');
  if (header && typeof window.ResizeObserver === 'function') {
    headerResizeObserver = new window.ResizeObserver(scheduleNavigationProgressPositionUpdate);
    headerResizeObserver.observe(header);
  }

  window.addEventListener('resize', scheduleNavigationProgressPositionUpdate, { passive: true });
  window.addEventListener('scroll', scheduleNavigationProgressPositionUpdate, { passive: true });

  if (hasPendingNavigation()) startNavigationProgress();

  document.addEventListener('click', (event) => {
    const anchor = event.target?.closest?.('a[href]');

    afterEventDispatch(() => {
      if (!event.defaultPrevented && isInternalPageLink(anchor, event)) startNavigationProgress();
    });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;

    afterEventDispatch(() => {
      // AJAX components prevent the native submit and retain their own local loading feedback.
      if (!event.defaultPrevented && isPageForm(form)) startNavigationProgress();
    });
  });

  window.addEventListener('beforeunload', startNavigationProgress);
  window.addEventListener('load', finishNavigationProgress);
  window.addEventListener('pageshow', finishNavigationProgress);
})();
